import { useMemo } from "react";

import { IDropOffLeadRow } from "../../models/DropOffPlanner";
import { ILocationGroupAvailability } from "../../models/LocationGroup";
import classNames from "../../utils/classNames";

interface DropOffStatsRowProps {
  unscheduledLeads: IDropOffLeadRow[];
  scheduledLeads: IDropOffLeadRow[];
  locationGroups: ILocationGroupAvailability[];
}

const capacityTextClass = (used: number, capacity: number): string => {
  if (capacity === 0) return "text-gray-500";
  const ratio = used / capacity;
  if (ratio >= 1) return "text-red-600";
  if (ratio >= 0.8) return "text-amber-600";
  return "text-gray-800";
};

const capacityBarClass = (used: number, capacity: number): string => {
  if (capacity === 0) return "bg-gray-300";
  const ratio = used / capacity;
  if (ratio >= 1) return "bg-red-500";
  if (ratio >= 0.8) return "bg-amber-500";
  return "bg-green-500";
};

export default function DropOffStatsRow({
  unscheduledLeads,
  scheduledLeads,
  locationGroups,
}: DropOffStatsRowProps) {
  const awaitingCount = unscheduledLeads.length;

  const totalCapacity = useMemo(
    () => locationGroups.reduce((sum, g) => sum + g.capacity, 0),
    [locationGroups]
  );
  const totalUsed = useMemo(
    () => locationGroups.reduce((sum, g) => sum + g.vehicleCount, 0),
    [locationGroups]
  );

  const sourceCounts = useMemo(() => {
    const counts = new Map<string, { booked: number; unbooked: number }>();
    const bump = (source: string | null, key: "booked" | "unbooked") => {
      const name = source && source.trim().length > 0 ? source : "Unknown";
      const entry = counts.get(name) ?? { booked: 0, unbooked: 0 };
      entry[key]++;
      counts.set(name, entry);
    };
    scheduledLeads.forEach((l) => bump(l.source, "booked"));
    unscheduledLeads.forEach((l) => bump(l.source, "unbooked"));
    return Array.from(counts.entries())
      .map(([name, v]) => ({ name, ...v, total: v.booked + v.unbooked }))
      .sort((a, b) => b.total - a.total);
  }, [scheduledLeads, unscheduledLeads]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
      <div className="bg-white border border-gray-200 rounded-md px-3 py-2 flex flex-col">
        <span className="text-[11px] uppercase tracking-wide text-gray-500">
          Awaiting Scheduling
        </span>
        <span
          className={classNames(
            "text-2xl font-semibold mt-0.5",
            awaitingCount > 0 ? "text-amber-600" : "text-gray-800"
          )}
        >
          {awaitingCount}
        </span>
      </div>

      <div className="bg-white border border-gray-200 rounded-md px-3 py-2 flex flex-col min-h-[88px]">
        <div className="flex items-baseline justify-between">
          <span className="text-[11px] uppercase tracking-wide text-gray-500">
            Yard Location Capacity
          </span>
          <span className={classNames("text-sm font-semibold", capacityTextClass(totalUsed, totalCapacity))}>
            {totalUsed} / {totalCapacity}
          </span>
        </div>
        <div className="mt-1 flex-1 overflow-y-auto space-y-1 pr-1 max-h-32">
          {locationGroups.length === 0 ? (
            <div className="text-xs text-gray-400">No location groups</div>
          ) : (
            locationGroups.map((g) => (
              <div key={g.locationGroupId} className="flex items-center gap-2 text-xs">
                <span className="flex-1 truncate text-gray-700">{g.name}</span>
                <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={classNames(
                      "h-full",
                      capacityBarClass(g.vehicleCount, g.capacity)
                    )}
                    style={{
                      width:
                        g.capacity > 0
                          ? `${Math.min(100, (g.vehicleCount / g.capacity) * 100)}%`
                          : "0%",
                    }}
                  />
                </div>
                <span
                  className={classNames(
                    "tabular-nums text-[11px] w-12 text-right",
                    capacityTextClass(g.vehicleCount, g.capacity)
                  )}
                >
                  {g.vehicleCount} / {g.capacity}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-md px-3 py-2 flex flex-col min-h-[88px]">
        <div className="flex items-baseline justify-between">
          <span className="text-[11px] uppercase tracking-wide text-gray-500">
            Vehicles by Source
          </span>
          <span className="text-[11px] text-gray-500">booked / unbooked</span>
        </div>
        <div className="mt-1 flex-1 overflow-y-auto space-y-1 pr-1 max-h-32">
          {sourceCounts.length === 0 ? (
            <div className="text-xs text-gray-400">No drop offs yet</div>
          ) : (
            sourceCounts.map((s) => (
              <div key={s.name} className="flex items-center gap-2 text-xs">
                <span className="flex-1 truncate text-gray-700">{s.name}</span>
                <span className="tabular-nums text-[11px] text-gray-800 w-16 text-right">
                  <span className="font-semibold">{s.booked}</span>
                  <span className="text-gray-400"> / </span>
                  <span className="text-gray-500">{s.unbooked}</span>
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
